import React, { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

/**
 * Floating "Install app" prompt for PWA.
 * - On Android/Chrome: uses the native beforeinstallprompt event → 1-tap install
 * - On iOS Safari: shows instructions modal (Safari doesn't support programmatic install)
 * - Dismisses persistently for 7 days via localStorage
 */
const DISMISS_KEY = "wr_install_dismissed_until";
const DISMISS_DAYS = 7;

export default function InstallAppPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  // Detect iOS Safari (not Chrome on iOS, not standalone-installed)
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isInStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  useEffect(() => {
    // Already installed → don't show
    if (isInStandalone) return;
    // User dismissed recently
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until > Date.now()) return;

    // Android/Chromium path
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari fallback — show after a short delay
    if (isIos) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () =>
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [isIos, isInStandalone]);

  const dismiss = () => {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
    );
    setVisible(false);
    setShowIosSheet(false);
  };

  const install = async () => {
    if (deferredEvent) {
      deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      if (choice.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
      setDeferredEvent(null);
    } else if (isIos) {
      setShowIosSheet(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] bg-brand-ink text-white shadow-2xl border border-brand-gold/30"
        data-testid="pwa-install-prompt"
      >
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
            <DownloadSimple size={20} weight="bold" className="text-brand-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-brand-gold font-semibold">
              Install the app
            </div>
            <div className="font-serif text-lg leading-tight mt-1">
              Add WR Rishtey to your home screen
            </div>
            <div className="text-xs text-white/70 mt-1 leading-snug">
              Faster access, private, and offline-ready.
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={install}
                data-testid="pwa-install-btn"
                className="text-[11px] uppercase tracking-widest px-3 py-1.5 bg-brand-gold text-brand-ink font-semibold rounded-full hover:bg-brand-gold/90"
              >
                Install
              </button>
              <button
                onClick={dismiss}
                data-testid="pwa-dismiss-btn"
                className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/30 text-white rounded-full hover:bg-white/10"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-close-btn"
            className="text-white/60 hover:text-white flex-shrink-0"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/60"
          onClick={dismiss}
        >
          <div
            className="bg-white w-full md:max-w-md md:m-6 p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-ios-sheet"
          >
            <div className="flex items-center gap-2">
              <DeviceMobile size={22} weight="duotone" className="text-brand-terracotta" />
              <div className="text-[11px] uppercase tracking-widest text-brand-terracotta font-semibold">
                Install on iPhone
              </div>
            </div>
            <h2 className="font-serif text-2xl mt-3 text-brand-ink leading-tight">
              Add to your home screen in 3 taps.
            </h2>
            <ol className="mt-5 space-y-4 text-sm text-brand-ink">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-bone text-brand-ink flex items-center justify-center flex-shrink-0 text-xs font-semibold">1</span>
                <span>Tap the <strong>Share</strong> button at the bottom of Safari.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-bone text-brand-ink flex items-center justify-center flex-shrink-0 text-xs font-semibold">2</span>
                <span>Scroll and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-bone text-brand-ink flex items-center justify-center flex-shrink-0 text-xs font-semibold">3</span>
                <span>Tap <strong>Add</strong> in the top-right. Done.</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              data-testid="pwa-ios-close"
              className="mt-6 w-full py-3 bg-brand-terracotta text-white text-[11px] uppercase tracking-widest rounded-full hover:bg-brand-terracotta/90"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
